export type ClassOverrides = Record<string, Record<string, string>>;

export type CustomizerState = {
  classOverrides: ClassOverrides;
};

/** Resolve a CSS color value (oklch, var(), rgb, etc.) to hex via a temp element */
export function resolveColorToHex(value: string): string {
  if (!value) return "#000000";
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;

  try {
    const temp = document.createElement("div");
    temp.style.color = value;
    temp.style.display = "none";
    document.body.appendChild(temp);
    const computed = getComputedStyle(temp).color;
    document.body.removeChild(temp);

    const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = parseInt(match[1]).toString(16).padStart(2, "0");
      const g = parseInt(match[2]).toString(16).padStart(2, "0");
      const b = parseInt(match[3]).toString(16).padStart(2, "0");
      return `#${r}${g}${b}`;
    }
  } catch {
    // Fallback
  }
  return "#000000";
}

/**
 * Check if a CSS selectorText targets a given class name.
 * Handles comma-separated selectors, descendant selectors, pseudo-classes.
 * Avoids false positives (e.g. .festapp-card won't match .festapp-card-image).
 */
function selectorMatchesClass(selectorText: string, className: string): boolean {
  // Match .className not followed by a word char or hyphen (avoids partial matches)
  const pattern = new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\w-])`);
  return pattern.test(selectorText);
}

/**
 * Read CSS properties declared for a given .festapp-* class from stylesheets.
 * Matches any rule whose selector targets the class (including grouped/compound selectors).
 * Skips our own injected style element and media queries.
 */
export function getClassProperties(
  className: string
): Record<string, string> {
  const props: Record<string, string> = {};

  function scanRules(rules: CSSRuleList) {
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSMediaRule) {
        scanRules(rule.cssRules);
        continue;
      }
      if (!(rule instanceof CSSStyleRule)) continue;
      if (!selectorMatchesClass(rule.selectorText, className)) continue;

      for (let i = 0; i < rule.style.length; i++) {
        const prop = rule.style[i];
        // Skip CSS custom properties — those are variable definitions, not visual properties
        if (prop.startsWith("--")) continue;
        props[prop] = rule.style.getPropertyValue(prop).trim();
      }
    }
  }

  try {
    for (const sheet of Array.from(document.styleSheets)) {
      // Skip our injected style element
      if (
        sheet.ownerNode instanceof HTMLElement &&
        sheet.ownerNode.id === "festapp-customizer-injected"
      ) {
        continue;
      }

      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }

      scanRules(rules);
    }
  } catch {
    // Fallback
  }

  return props;
}

/** Build CSS text from class overrides */
export function classOverridesToCSS(
  overrides: ClassOverrides,
  important = false
): string {
  const blocks: string[] = [];
  const suffix = important ? " !important" : "";
  for (const [selector, props] of Object.entries(overrides)) {
    const entries = Object.entries(props);
    if (entries.length === 0) continue;
    const body = entries
      .map(([p, v]) => `  ${p}: ${v}${suffix};`)
      .join("\n");
    blocks.push(`.${selector} {\n${body}\n}`);
  }
  return blocks.join("\n\n");
}
